'use client';

import type { ReactNode } from 'react';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Icon } from '@/components/ui/icon';
import { Loading03Icon } from '@/components/ui/icon/glyphs';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { cn } from '@/lib/ui/utils';

const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

export type InfiniteListScrollMode = 'contained' | 'page-below-lg';

interface CommonProps {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void | Promise<void>;
  isError?: boolean;
  /** When true AND the list has no items (or children), render `loadingPlaceholder` instead of `emptyState`. */
  isInitialLoading?: boolean;
  loadingPlaceholder?: ReactNode;
  emptyState?: ReactNode;
  /** Cap on the scroll viewport. Number is px; string is any CSS length. Default `480`. */
  maxHeight?: number | string;
  /** Let the page own scrolling below the desktop (`lg`) breakpoint. Default `contained`. */
  scrollMode?: InfiniteListScrollMode;
  /** Size of the top/bottom fade mask. `0` disables. Default `24` (px). */
  fadeSize?: number;
  /** Class applied to the scroll container (outer). */
  className?: string;
  'data-testid'?: string;
}

interface ItemsModeProps<T> extends CommonProps {
  items: readonly T[];
  renderItem: (item: T, index: number) => ReactNode;
  getItemKey: (item: T, index: number) => string;
  /** Optional separator between items. Constant node or per-index callback. */
  separator?: ReactNode | ((index: number) => ReactNode | null);
  /** Class applied to the inner items column. Default `flex flex-col gap-3`. */
  listClassName?: string;
  children?: never;
}

interface ChildrenModeProps extends CommonProps {
  /** Escape valve: bring your own markup (tables, grids, etc). The scroll
   * container + fade mask + sentinel still apply, but item rendering, empty
   * state, and separators are the caller's responsibility. */
  children: ReactNode;
  items?: never;
  renderItem?: never;
  getItemKey?: never;
  separator?: never;
  listClassName?: never;
}

type InfiniteListProps<T> = ItemsModeProps<T> | ChildrenModeProps;

export function InfiniteList<T = unknown>(props: InfiniteListProps<T>): React.ReactNode {
  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isError = false,
    isInitialLoading = false,
    loadingPlaceholder,
    emptyState,
    maxHeight = 480,
    scrollMode = 'contained',
    fadeSize = 24,
    className,
    'data-testid': testId,
  } = props;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [fadeTop, setFadeTop] = useState(false);
  const [fadeBottom, setFadeBottom] = useState(false);
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const usesContainedScroll = scrollUsesContainedViewport(scrollMode, isDesktop);

  useEffect(() => {
    if (!usesContainedScroll) return;
    const root = scrollRef.current;
    if (root === null) return;
    const update = (): void => {
      const canScrollUp = root.scrollTop > 1;
      const canScrollDown = root.scrollTop + root.clientHeight < root.scrollHeight - 1;
      setFadeTop(canScrollUp);
      setFadeBottom(canScrollDown);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(root);
    root.addEventListener('scroll', update, { passive: true });
    return (): void => {
      ro.disconnect();
      root.removeEventListener('scroll', update);
    };
  }, [usesContainedScroll]);

  const fetchNextPageRef = useRef(fetchNextPage);
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  const isErrorRef = useRef(isError);

  useLayoutEffect(() => {
    fetchNextPageRef.current = fetchNextPage;
    hasNextPageRef.current = hasNextPage;
    isFetchingNextPageRef.current = isFetchingNextPage;
    isErrorRef.current = isError;
  }, [fetchNextPage, hasNextPage, isError, isFetchingNextPage]);

  // Re-observe after a page settles even when filtering kept the sentinel in view.
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isError) return;
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (root === null || sentinel === null) return;

    let firing = false;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting !== true) return;
        if (
          !hasNextPageRef.current ||
          isFetchingNextPageRef.current ||
          isErrorRef.current ||
          firing
        ) {
          return;
        }
        firing = true;
        const result = fetchNextPageRef.current();
        const reset = (): void => {
          firing = false;
        };
        if (result instanceof Promise) {
          result.then(reset, reset);
        } else {
          reset();
        }
      },
      { root: usesContainedScroll ? root : null, rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return (): void => {
      observer.disconnect();
    };
  }, [hasNextPage, isError, isFetchingNextPage, usesContainedScroll]);

  const isItemsMode = props.items !== undefined;
  const hasContent = isItemsMode ? props.items.length > 0 : props.children !== undefined;
  const showInitialLoading = isInitialLoading && !hasContent;
  const showEmpty = !showInitialLoading && isItemsMode && props.items.length === 0;

  const maxHeightValue = typeof maxHeight === 'number' ? `${String(maxHeight)}px` : maxHeight;
  const fadeMask = usesContainedScroll ? buildFadeMask(fadeSize, fadeTop, fadeBottom) : undefined;
  const pageScrollsBelowLg = scrollMode === 'page-below-lg';

  let body: ReactNode;
  if (showInitialLoading) {
    body = loadingPlaceholder ?? <ListSpinner />;
  } else if (showEmpty) {
    body = emptyState;
  } else if (isItemsMode) {
    const { items, renderItem, getItemKey, separator, listClassName } = props;
    body = (
      <div className={cn('flex flex-col gap-3', listClassName)}>
        {items.map((item, index) => {
          const key = getItemKey(item, index);
          const sep =
            typeof separator === 'function' ? separator(index) : index > 0 ? separator : null;
          return (
            <Fragment key={key}>
              {sep}
              {renderItem(item, index)}
            </Fragment>
          );
        })}
      </div>
    );
  } else {
    body = props.children;
  }

  return (
    <div
      ref={scrollRef}
      // `overflow-y-auto` on its own makes the browser compute `overflow-x`
      // to `auto` (per the CSS overflow spec: a non-`visible` axis forces the
      // other axis from `visible` to `auto`). That surfaced a stray
      // horizontal scrollbar whenever a row was even sub-pixel wider than the
      // viewport. A vertical infinite list must never scroll sideways — clip
      // the cross-axis explicitly.
      className={cn(
        pageScrollsBelowLg
          ? 'overflow-visible lg:max-h-[var(--infinite-list-max-height)] lg:overflow-x-hidden lg:overflow-y-auto'
          : 'overflow-x-hidden overflow-y-auto',
        className,
      )}
      style={{
        ...(pageScrollsBelowLg
          ? { ['--infinite-list-max-height' as string]: maxHeightValue }
          : { maxHeight: maxHeightValue }),
        maskImage: fadeMask,
        WebkitMaskImage: fadeMask,
      }}
      data-testid={testId}
    >
      {body}
      <div ref={sentinelRef} className="h-1" aria-hidden />
      {isFetchingNextPage ? (
        <div className="flex justify-center py-3">
          <ListSpinner />
        </div>
      ) : null}
    </div>
  );
}

function scrollUsesContainedViewport(
  scrollMode: InfiniteListScrollMode,
  isDesktop: boolean,
): boolean {
  switch (scrollMode) {
    case 'contained':
      return true;
    case 'page-below-lg':
      return isDesktop;
    default: {
      const _exhaustive: never = scrollMode;
      return _exhaustive;
    }
  }
}

function ListSpinner(): React.ReactNode {
  return (
    <Icon
      icon={Loading03Icon}
      className="size-5 animate-spin text-foreground-secondary motion-reduce:animate-none"
    />
  );
}

function buildFadeMask(size: number, top: boolean, bottom: boolean): string | undefined {
  if (size <= 0 || (!top && !bottom)) return undefined;
  const topStop = top ? `transparent 0, black ${String(size)}px` : 'black 0';
  const bottomStop = bottom
    ? `black calc(100% - ${String(size)}px), transparent 100%`
    : 'black 100%';
  return `linear-gradient(to bottom, ${topStop}, ${bottomStop})`;
}
